import {
  AlertCircle,
  CheckCircle2,
  FileImage,
  Images,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  actionButtonClassName,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import type {
  WatermarkDecodeConfidence,
  WatermarkDecodeResult,
} from "../types";
import { formatDateTime } from "../utils";

const MAX_ITEMS = 20;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_PROCESS_DIMENSION = 1800;
const WORKER_COUNT = 2;

type QueueStatus = "queued" | "processing" | "complete" | "error";

interface QueueItem {
  id: string;
  file: File;
  previewUrl: string;
  status: QueueStatus;
  result: WatermarkDecodeResult | null;
  error: string | null;
  sourceWidth: number | null;
  sourceHeight: number | null;
}

interface PreparedImage {
  blob: Blob;
  sourceWidth: number;
  sourceHeight: number;
  processScale: number;
}

export function WatermarksPage() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const itemsRef = useRef(items);
  const runningRef = useRef(false);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(
    () => () => {
      for (const item of itemsRef.current) {
        URL.revokeObjectURL(item.previewUrl);
      }
    },
    [],
  );

  const addFiles = useCallback((incoming: File[]) => {
    const images = incoming.filter(isImageFile);
    if (!images.length) {
      toast.error("请选择图片文件");
      return;
    }
    const available = Math.max(0, MAX_ITEMS - itemsRef.current.length);
    const accepted = images.slice(0, available);
    if (!accepted.length) {
      toast.error(`一次最多处理 ${MAX_ITEMS} 张图片`);
      return;
    }
    const queued = accepted.map(createQueueItem);
    setItems((current) => {
      const next = [...current, ...queued];
      itemsRef.current = next;
      return next;
    });
    if (images.length > accepted.length) {
      toast.warning(`已加入 ${accepted.length} 张，其余图片超出数量上限`);
    } else {
      toast.success(`已加入 ${accepted.length} 张图片`);
    }
  }, []);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.items || [])
        .filter(
          (item) => item.kind === "file" && item.type.startsWith("image/"),
        )
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file))
        .map((file, index) => clipboardFile(file, index));
      if (!pasted.length) return;
      event.preventDefault();
      addFiles(pasted);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [addFiles]);

  const patchItem = useCallback(
    (
      id: string,
      patch: Partial<Omit<QueueItem, "id" | "file" | "previewUrl">>,
    ) => {
      setItems((current) => {
        const next = current.map((item) =>
          item.id === id ? { ...item, ...patch } : item,
        );
        itemsRef.current = next;
        return next;
      });
    },
    [],
  );

  const analyzeOne = useCallback(
    async (item: QueueItem) => {
      patchItem(item.id, {
        status: "processing",
        error: null,
        result: null,
      });
      try {
        const prepared = await prepareImage(item.file);
        patchItem(item.id, {
          sourceWidth: prepared.sourceWidth,
          sourceHeight: prepared.sourceHeight,
        });
        const result = await api.decodeWatermark(prepared.blob, {
          fileName: item.file.name || "clipboard-image.png",
          processScale: prepared.processScale,
        });
        patchItem(item.id, { status: "complete", result, error: null });
      } catch (error) {
        patchItem(item.id, {
          status: "error",
          result: null,
          error: error instanceof Error ? error.message : "图片识别失败",
        });
      }
    },
    [patchItem],
  );

  const runBatch = useCallback(
    async (requestedIds?: string[]) => {
      if (runningRef.current) return;
      const requested = requestedIds ? new Set(requestedIds) : null;
      const queued = itemsRef.current.filter(
        (item) =>
          (requested ? requested.has(item.id) : item.status === "queued") &&
          item.status !== "processing",
      );
      if (!queued.length) {
        toast.error("请先加入待识别图片");
        return;
      }
      runningRef.current = true;
      setRunning(true);
      try {
        await runWithConcurrency(queued, WORKER_COUNT, analyzeOne);
      } finally {
        runningRef.current = false;
        setRunning(false);
      }
    },
    [analyzeOne],
  );

  useEffect(() => {
    if (
      running ||
      runningRef.current ||
      !items.some((item) => item.status === "queued")
    ) {
      return;
    }
    void runBatch();
  }, [items, runBatch, running]);

  const removeItem = (item: QueueItem) => {
    if (runningRef.current || item.status === "processing") return;
    URL.revokeObjectURL(item.previewUrl);
    setItems((current) => {
      const next = current.filter((candidate) => candidate.id !== item.id);
      itemsRef.current = next;
      return next;
    });
  };

  const clearItems = () => {
    if (runningRef.current) return;
    for (const item of itemsRef.current) {
      URL.revokeObjectURL(item.previewUrl);
    }
    itemsRef.current = [];
    setItems([]);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) addFiles(files);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    addFiles(Array.from(event.dataTransfer.files));
  };

  const decodedCount = items.filter(
    (item) => item.result?.status === "decoded",
  ).length;
  const finishedCount = items.filter(
    (item) => item.status === "complete" || item.status === "error",
  ).length;

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="SCREENMARK"
        title="水印识别"
        desc="上传或粘贴小程序截图，批量读取加密身份水印"
        className="rise-in"
      />

      <Panel className="rise-in rise-in-1 overflow-hidden">
        <div
          className={`relative flex min-h-[260px] flex-col items-center justify-center border-2 border-dashed px-6 py-10 text-center transition-colors ${
            dragging
              ? "border-clay bg-clay-soft/55"
              : "border-transparent bg-cream/45 hover:bg-cream/70"
          }`}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (
              !event.currentTarget.contains(event.relatedTarget as Node | null)
            ) {
              setDragging(false);
            }
          }}
          onDrop={onDrop}
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-paper text-clay-deep shadow-sm">
            <Images size={24} strokeWidth={1.65} />
          </div>
          <h3 className="mt-5 font-display text-[18px] font-medium text-ink">
            拖入图片或直接粘贴
          </h3>
          <p className="mt-2 max-w-md text-[12.5px] leading-6 text-ink-soft">
            支持一次选择多张图片，也可从截图工具或聊天窗口复制后按 Ctrl + V
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2.5">
            <label
              className={actionButtonClassName(
                "light",
                "relative cursor-pointer overflow-hidden focus-within:ring-2 focus-within:ring-clay focus-within:ring-offset-2",
              )}
            >
              <Upload size={13.5} aria-hidden="true" />
              <span>选择图片</span>
              {/* Keep the native input under the touch target for mobile pickers. */}
              <input
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                type="file"
                aria-label="选择图片"
                accept="image/*"
                multiple
                onChange={onFileChange}
              />
            </label>
          </div>
        </div>
        <div className="flex items-start gap-3 border-t border-sand-light/70 bg-paper px-5 py-4">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-sage" />
          <div>
            <p className="text-[12.5px] font-medium text-ink">图片不会保存</p>
            <p className="mt-0.5 text-[11.5px] leading-5 text-ink-faint">
              图片只在本次识别中传给服务端，处理完成后不写入磁盘、对象存储或数据库。
            </p>
          </div>
        </div>
      </Panel>

      {items.length ? (
        <section className="rise-in rise-in-2 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="eyebrow text-[10px] text-ink-faint">BATCH</p>
              <h3 className="mt-1 font-display text-[17px] font-medium text-ink">
                识别队列
              </h3>
              <p className="mt-1 text-[11.5px] text-ink-faint">
                {finishedCount}/{items.length} 已处理
                {decodedCount ? ` · ${decodedCount} 张已识别` : ""}
              </p>
            </div>
            <ActionButton tone="ghost" disabled={running} onClick={clearItems}>
              <Trash2 size={13} />
              清空
            </ActionButton>
          </div>

          <div className="grid gap-3">
            {items.map((item) => (
              <WatermarkQueueCard
                key={item.id}
                item={item}
                running={running}
                onRemove={() => removeItem(item)}
                onRetry={() => void runBatch([item.id])}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function WatermarkQueueCard({
  item,
  running,
  onRemove,
  onRetry,
}: {
  item: QueueItem;
  running: boolean;
  onRemove: () => void;
  onRetry: () => void;
}) {
  return (
    <Panel className="overflow-hidden">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
        <div className="relative h-28 w-full shrink-0 overflow-hidden rounded-xl bg-cream-deep sm:h-24 sm:w-24">
          <img
            src={item.previewUrl}
            alt=""
            className="h-full w-full object-cover"
          />
          {item.status === "processing" ? (
            <span className="absolute inset-0 flex items-center justify-center bg-ink/45 text-paper">
              <LoaderCircle size={20} className="animate-spin" />
            </span>
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <FileImage size={14} className="shrink-0 text-ink-faint" />
            <p className="max-w-full truncate text-[13px] font-medium text-ink">
              {item.file.name || "粘贴图片"}
            </p>
            <QueueTag item={item} />
          </div>
          <p className="mt-1 text-[10.5px] text-ink-faint">
            {item.sourceWidth && item.sourceHeight
              ? `${item.sourceWidth} × ${item.sourceHeight} px · `
              : ""}
            {formatBytes(item.file.size)}
          </p>
          <div aria-live="polite" aria-atomic="true">
            <QueueResult item={item} />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 self-end sm:self-start">
          {item.status === "error" ||
          (item.status === "complete" && item.result?.status !== "decoded") ? (
            <button
              type="button"
              className="rounded-lg p-2 text-ink-faint transition hover:bg-peach hover:text-clay-deep disabled:opacity-35"
              disabled={running}
              onClick={onRetry}
              title="重新识别"
              aria-label="重新识别"
            >
              <RefreshCw size={14} />
            </button>
          ) : null}
          <button
            type="button"
            className="rounded-lg p-2 text-ink-faint transition hover:bg-[#F3DDD3] hover:text-destructive disabled:opacity-35"
            disabled={running || item.status === "processing"}
            onClick={onRemove}
            title="移除"
            aria-label="移除图片"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    </Panel>
  );
}

function QueueTag({ item }: { item: QueueItem }) {
  if (item.status === "queued") return <Tag>待识别</Tag>;
  if (item.status === "processing") return <Tag tone="clay">识别中</Tag>;
  if (item.status === "error") return <Tag tone="danger">失败</Tag>;
  if (item.result?.status === "decoded") return <Tag tone="sage">已识别</Tag>;
  if (item.result?.status === "unrecognized") {
    return <Tag tone="danger">无法解密</Tag>;
  }
  return <Tag>未识别</Tag>;
}

function QueueResult({ item }: { item: QueueItem }) {
  if (item.status === "queued") {
    return <p className="mt-3 text-[12px] text-ink-soft">等待识别</p>;
  }
  if (item.status === "processing") {
    return <p className="mt-3 text-[12px] text-clay-deep">正在扫描水印…</p>;
  }
  if (item.status === "error") {
    return (
      <p className="mt-3 flex items-start gap-2 text-[12px] text-destructive">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        {item.error || "图片识别失败"}
      </p>
    );
  }
  const result = item.result;
  if (!result) return null;
  if (result.status === "decoded") {
    return (
      <div className="mt-3 rounded-xl border border-[#D7DFC9] bg-[#F2F5ED] px-4 py-3">
        <div className="flex items-center gap-2 text-sage">
          <CheckCircle2 size={15} />
          <span className="text-[11px] font-medium">解密成功</span>
        </div>
        <p className="mt-1.5 font-display text-[18px] font-medium tracking-wide text-ink">
          {result.content}
        </p>
        {result.generatedAt ? (
          <p className="mt-1 text-[11px] text-ink-faint">
            生成于 {formatDateTime(result.generatedAt)}
          </p>
        ) : null}
        {result.confidence ? (
          <ConfidenceMetrics confidence={result.confidence} />
        ) : null}
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-start gap-2 rounded-xl bg-cream/70 px-4 py-3">
      <AlertCircle size={15} className="mt-0.5 shrink-0 text-ink-faint" />
      <div>
        <p className="text-[12.5px] font-medium text-ink">
          {result.status === "unrecognized"
            ? "检测到水印，但无法解密"
            : "未识别到有效水印"}
        </p>
        <p className="mt-1 text-[11px] leading-5 text-ink-faint">
          {result.status === "unrecognized"
            ? "截图可能来自其他环境或使用了已更换的服务端密钥。"
            : "请尝试范围更完整、未经压缩的原始截图。"}
        </p>
        {result.confidence ? (
          <ConfidenceMetrics confidence={result.confidence} />
        ) : null}
      </div>
    </div>
  );
}

function ConfidenceMetrics({
  confidence,
}: {
  confidence: WatermarkDecodeConfidence;
}) {
  return (
    <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[9.5px] text-ink-faint">
      <span>同步 {Math.round(confidence.syncScore * 100)}%</span>
      <span>覆盖 {Math.round(confidence.syncCoverage * 100)}%</span>
      <span>观测 {Math.round(confidence.observedFraction * 100)}%</span>
      <span>模块 {confidence.cellSize}px</span>
    </div>
  );
}

function createQueueItem(file: File): QueueItem {
  return {
    id: globalThis.crypto.randomUUID(),
    file,
    previewUrl: URL.createObjectURL(file),
    status: "queued",
    result: null,
    error: null,
    sourceWidth: null,
    sourceHeight: null,
  };
}

function clipboardFile(file: File, index: number): File {
  const usefulName = file.name && !/^image\.(png|jpe?g|webp)$/i.test(file.name);
  if (usefulName) return file;
  const extension = mimeExtension(file.type);
  return new File([file], `粘贴图片-${Date.now()}-${index + 1}.${extension}`, {
    type: file.type || "image/png",
    lastModified: Date.now(),
  });
}

function mimeExtension(type: string): string {
  if (type === "image/jpeg") return "jpg";
  if (type === "image/webp") return "webp";
  if (type === "image/gif") return "gif";
  return "png";
}

function isImageFile(file: File): boolean {
  return (
    file.type.startsWith("image/") ||
    /\.(?:png|jpe?g|webp|gif|bmp|avif|heic|heif)$/i.test(file.name)
  );
}

async function prepareImage(file: File): Promise<PreparedImage> {
  const image = await decodeImage(file);
  try {
    const baseScale = Math.min(
      1,
      MAX_PROCESS_DIMENSION / Math.max(image.width, image.height),
    );
    let scale = baseScale;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("当前浏览器无法读取图片");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(image.source, 0, 0, width, height);
      const blob = await canvasToPng(canvas);
      canvas.width = 1;
      canvas.height = 1;
      if (blob.size <= MAX_IMAGE_BYTES) {
        return {
          blob,
          sourceWidth: image.width,
          sourceHeight: image.height,
          processScale: width / image.width,
        };
      }
      scale *= 0.78;
    }
    throw new Error("图片内容过大，请先裁剪后再识别");
  } finally {
    image.close();
  }
}

async function decodeImage(file: File): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}> {
  if (typeof createImageBitmap === "function") {
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      throw new Error("图片读取失败，请重新选择");
    }
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
    };
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;
  try {
    await image.decode();
  } catch {
    URL.revokeObjectURL(url);
    throw new Error("图片读取失败，请重新选择");
  }
  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    close: () => URL.revokeObjectURL(url),
  };
}

function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("图片转换失败，请重新选择"));
    }, "image/png");
  });
}

async function runWithConcurrency<T>(
  values: T[],
  limit: number,
  task: (value: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, values.length) }, async () => {
      while (cursor < values.length) {
        const index = cursor;
        cursor += 1;
        await task(values[index]);
      }
    }),
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}
